import { useState, useEffect } from 'react';
import { Search, Bell, TrendingUp, AlertTriangle } from 'lucide-react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { ContentRecord, AIReadinessStat } from '../../types/content';
import { MetricCard, EmptyState } from '../shared';

interface GapFrequency {
  gap: string;
  count: number;
  affectedRecords: number;
}

export function AIReadiness() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<ContentRecord[]>([]);
  const [readinessByType, setReadinessByType] = useState<AIReadinessStat[]>([]);
  const [commonGaps, setCommonGaps] = useState<GapFrequency[]>([]);

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'contentRecords'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const recordsData = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as ContentRecord);
      setRecords(recordsData);

      // Calculate readiness by type
      const typeStats: Record<string, AIReadinessStat> = {};
      recordsData.forEach(record => {
        if (!typeStats[record.contentType]) {
          typeStats[record.contentType] = {
            contentType: record.contentType,
            total: 0,
            excellent: 0,
            good: 0,
            needsWork: 0,
            poor: 0,
            averageScore: 0
          };
        }
        const stat = typeStats[record.contentType];
        stat.total++;
        stat[record.aiReadiness]++;
        stat.averageScore += record.aiReadinessScore;
      });

      const statsArray = Object.values(typeStats).map(stat => ({
        ...stat,
        averageScore: Math.round(stat.averageScore / stat.total)
      }));
      setReadinessByType(statsArray);

      // Calculate common gaps
      const gapCounts: Record<string, GapFrequency> = {};
      recordsData.forEach(record => {
        record.gaps.forEach(gap => {
          if (!gapCounts[gap]) {
            gapCounts[gap] = { gap, count: 0, affectedRecords: 0 };
          }
          gapCounts[gap].count++;
          gapCounts[gap].affectedRecords++;
        });
      });

      const gapsArray = Object.values(gapCounts)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
      setCommonGaps(gapsArray);

    } catch (error) {
      console.error('Error loading AI readiness data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalRecords = records.length;
  const excellentCount = records.filter(r => r.aiReadiness === 'excellent').length;
  const goodCount = records.filter(r => r.aiReadiness === 'good').length;
  const needsWorkCount = records.filter(r => r.aiReadiness === 'needsWork').length;
  const poorCount = records.filter(r => r.aiReadiness === 'poor').length;
  const averageScore = totalRecords > 0
    ? Math.round(records.reduce((sum, r) => sum + r.aiReadinessScore, 0) / totalRecords)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t2-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Content</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">AI Readiness</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t2-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t2-primary uppercase tracking-wide mb-1">Kodiac Content</p>
          <h1 className="text-3xl font-semibold text-gray-900">AI Readiness</h1>
          <p className="text-sm text-gray-600 mt-1">Track content quality and optimization for AI platforms</p>
        </div>

        {/* 5-Stat Row */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          <MetricCard
            label="Average Score"
            value={averageScore.toString()}
          />
          <MetricCard
            label="Excellent"
            value={excellentCount.toString()}
          />
          <MetricCard
            label="Good"
            value={goodCount.toString()}
          />
          <MetricCard
            label="Needs Work"
            value={needsWorkCount.toString()}
          />
          <MetricCard
            label="Poor"
            value={poorCount.toString()}
          />
        </div>

        {/* Readiness by Content Type */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Readiness by Content Type</h2>
          {readinessByType.length === 0 ? (
            <EmptyState
              icon={TrendingUp}
              title="No content data"
              description="Sync content to see readiness metrics"
            />
          ) : (
            <div className="space-y-6">
              {readinessByType.map(stat => (
                <div key={stat.contentType}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-gray-900 capitalize">
                        {stat.contentType}
                      </h3>
                      <span className="text-xs text-gray-600">
                        {stat.total} {stat.total === 1 ? 'record' : 'records'}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-t2-primary">
                        {stat.averageScore}
                      </div>
                      <div className="text-xs text-gray-500">avg score</div>
                    </div>
                  </div>

                  {/* Stacked Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-4 flex overflow-hidden mb-2">
                    <div
                      className="bg-success-green h-4"
                      style={{ width: `${(stat.excellent / stat.total) * 100}%` }}
                      title={`${stat.excellent} excellent`}
                    />
                    <div
                      className="bg-blue-400 h-4"
                      style={{ width: `${(stat.good / stat.total) * 100}%` }}
                      title={`${stat.good} good`}
                    />
                    <div
                      className="bg-warning-amber h-4"
                      style={{ width: `${(stat.needsWork / stat.total) * 100}%` }}
                      title={`${stat.needsWork} needs work`}
                    />
                    <div
                      className="bg-critical-red h-4"
                      style={{ width: `${(stat.poor / stat.total) * 100}%` }}
                      title={`${stat.poor} poor`}
                    />
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-6 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-success-green rounded"></div>
                      <span className="text-gray-600">
                        Excellent: <span className="font-medium text-gray-900">{stat.excellent}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-blue-400 rounded"></div>
                      <span className="text-gray-600">
                        Good: <span className="font-medium text-gray-900">{stat.good}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-warning-amber rounded"></div>
                      <span className="text-gray-600">
                        Needs Work: <span className="font-medium text-gray-900">{stat.needsWork}</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-critical-red rounded"></div>
                      <span className="text-gray-600">
                        Poor: <span className="font-medium text-gray-900">{stat.poor}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Common Gaps Panel */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-warning-amber" />
            <h2 className="text-base font-semibold text-gray-900">Most Common Content Gaps</h2>
          </div>

          {commonGaps.length === 0 ? (
            <EmptyState
              icon={AlertTriangle}
              title="No gaps detected"
              description="All content meets AI readiness standards"
            />
          ) : (
            <div className="space-y-3">
              {commonGaps.map((item, index) => (
                <div
                  key={item.gap}
                  className="flex items-center justify-between p-3 bg-surface-warm rounded-lg border border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-warning-amber text-white rounded-full text-sm font-semibold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.gap}</p>
                      <p className="text-xs text-gray-600">
                        Affects {item.affectedRecords} {item.affectedRecords === 1 ? 'record' : 'records'}
                      </p>
                    </div>
                  </div>
                  <button className="text-sm text-t2-primary hover:text-t2-dark font-medium">
                    Fix All →
                  </button>
                </div>
              ))}
            </div>
          )}

          {commonGaps.length > 0 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-900">
                <strong>Tip:</strong> Addressing these common gaps will improve AI readiness across{' '}
                {records.filter(r => r.gaps.length > 0).length} records.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
